/**
 * Takeovers dashboard — creator ad-takeover request pipeline.
 *
 * Tabs: Requests (pipeline + detail) | Creators (request-page management) |
 * Analytics. Deep links: ?request=<id> opens the detail modal (used by the
 * internal notification email), ?tab=<key> selects a tab. Creator payout
 * report pages live on the Creators dashboard (TakeoversRoute redirects the
 * old ?tab=creator-pages links).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, DashboardFrame, EmptyState, Spinner, StatusPill, Tabs, tableStyles } from '../ui';
import { usePermissions } from '../../hooks/usePermissions';
import { useDashboardToast } from '../../hooks/useDashboardToast';
import {
  RequestFilters,
  downloadTakeoverCsv,
  listTakeoverCreators,
  listTakeoverRequests,
} from '../../services/takeoversApi';
import RequestsTable, { RequestFiltersState } from './RequestsTable';
import RequestDetail from './RequestDetail';
import CreatorEditor from './CreatorEditor';
import AnalyticsPanel from './AnalyticsPanel';
import { fmtDate } from './shared';
import { TakeoverCreator, TakeoverRequest } from './types';

type TabKey = 'requests' | 'creators' | 'analytics';

const TAB_KEYS: TabKey[] = ['requests', 'creators', 'analytics'];

const TakeoversDashboard: React.FC = () => {
  const { has } = usePermissions();
  const canManage = has('takeovers.manage');

  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') as TabKey | null;
  const [tab, setTab] = useState<TabKey>(tabParam && TAB_KEYS.includes(tabParam) ? tabParam : 'requests');

  // One toast at a time, raised by every tab and modal through `notify`.
  const { notify, toastLayer } = useDashboardToast();

  // --- creators ---------------------------------------------------------------
  const [creators, setCreators] = useState<TakeoverCreator[]>([]);
  const [creatorsLoading, setCreatorsLoading] = useState(true);
  const [editingCreator, setEditingCreator] = useState<TakeoverCreator | null>(null);
  const [showCreateCreator, setShowCreateCreator] = useState(false);
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);

  const refreshCreators = useCallback(() => {
    setCreatorsLoading(true);
    listTakeoverCreators()
      .then(setCreators)
      .catch((err) => notify('error', 'Failed to load creators', err?.message))
      .finally(() => setCreatorsLoading(false));
  }, [notify]);

  useEffect(() => { refreshCreators(); }, [refreshCreators]);

  // --- requests ---------------------------------------------------------------
  const [requests, setRequests] = useState<TakeoverRequest[]>([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [filters, setFilters] = useState<RequestFiltersState>({ creatorId: '', status: '', createdFrom: '', createdTo: '' });
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);

  const toApiFilters = useCallback((f: RequestFiltersState, cursor?: string): RequestFilters => ({
    creatorId: f.creatorId || undefined,
    status: f.status || undefined,
    createdFrom: f.createdFrom || undefined,
    createdTo: f.createdTo || undefined,
    limit: 50,
    cursor,
  }), []);

  const loadRequests = useCallback((f: RequestFiltersState) => {
    setRequestsLoading(true);
    listTakeoverRequests(toApiFilters(f))
      .then((res) => {
        setRequests(res.requests);
        setNextCursor(res.nextCursor);
      })
      .catch((err) => notify('error', 'Failed to load requests', err?.message))
      .finally(() => setRequestsLoading(false));
  }, [notify, toApiFilters]);

  useEffect(() => { loadRequests(filters); }, [filters, loadRequests]);

  const loadMore = useCallback(() => {
    if (!nextCursor) return;
    setRequestsLoading(true);
    listTakeoverRequests(toApiFilters(filters, nextCursor))
      .then((res) => {
        setRequests((prev) => {
          const seen = new Set(prev.map((r) => r.id));
          return [...prev, ...res.requests.filter((r) => !seen.has(r.id))];
        });
        setNextCursor(res.nextCursor);
      })
      .catch((err) => notify('error', 'Failed to load more', err?.message))
      .finally(() => setRequestsLoading(false));
  }, [filters, nextCursor, notify, toApiFilters]);

  const exportCsv = useCallback(async () => {
    setExporting(true);
    try {
      await downloadTakeoverCsv({ creatorId: filters.creatorId || undefined, status: filters.status || undefined });
    } catch (err: any) {
      notify('error', 'Export failed', err?.message);
    } finally {
      setExporting(false);
    }
  }, [filters, notify]);

  // --- detail deep link ---------------------------------------------------------
  const openRequestId = searchParams.get('request');
  const openDetail = useCallback((id: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('request', id);
    setSearchParams(next, { replace: false });
  }, [searchParams, setSearchParams]);
  const closeDetail = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('request');
    setSearchParams(next, { replace: false });
  }, [searchParams, setSearchParams]);

  const onRequestUpdated = useCallback((updated: TakeoverRequest) => {
    setRequests((prev) => prev.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
  }, []);

  const changeTab = useCallback((next: TabKey) => {
    setTab(next);
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  }, [searchParams, setSearchParams]);

  const newCount = useMemo(
    () => requests.filter((r) => r.displayStatus === 'new').length,
    [requests],
  );

  const tabOptions = useMemo(
    (): Array<{ value: TabKey; label: React.ReactNode; count?: number }> => [
      { value: 'requests', label: 'Requests', count: newCount > 0 ? newCount : undefined },
      { value: 'creators', label: 'Creators' },
      { value: 'analytics', label: 'Analytics' },
    ],
    [newCount],
  );

  const copyCreatorLink = async (c: TakeoverCreator) => {
    await navigator.clipboard.writeText(c.publicUrl);
    setCopiedSlug(c.slug);
    setTimeout(() => setCopiedSlug(null), 1600);
  };

  return (
    <DashboardFrame
      eyebrow="CG Core"
      title="Takeovers"
      tabs={<Tabs value={tab} onChange={changeTab} options={tabOptions} variant="underlined" ariaLabel="Takeovers sections" />}
      topBarRight={
        tab === 'creators' && canManage ? (
          <Button variant="primary" size="sm" onClick={() => setShowCreateCreator(true)}>+ Add creator</Button>
        ) : undefined
      }
    >
      <div style={{ padding: '20px 0' }}>
        {tab === 'requests' && (
          <RequestsTable
            requests={requests}
            loading={requestsLoading}
            nextCursor={nextCursor}
            filters={filters}
            creators={creators}
            canManage={canManage}
            search={search}
            onSearchChange={setSearch}
            onFiltersChange={setFilters}
            onOpen={openDetail}
            onLoadMore={loadMore}
            onExport={exportCsv}
            exporting={exporting}
          />
        )}

        {tab === 'creators' && (
          creatorsLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spinner /></div>
          ) : creators.length === 0 ? (
            <EmptyState
              title="No creators yet"
              subtitle="Add a creator to generate their shareable takeover request page."
            />
          ) : (
            <div style={tableStyles.wrapScroll}>
              <table style={tableStyles.table}>
                <thead>
                  <tr>
                    <th style={tableStyles.th}>Creator</th>
                    <th style={tableStyles.th}>Request link</th>
                    <th style={tableStyles.th}>Contacts</th>
                    <th style={tableStyles.th}>Tiers</th>
                    <th style={tableStyles.th}>Status</th>
                    <th style={tableStyles.th}>Updated</th>
                    <th style={tableStyles.th} />
                  </tr>
                </thead>
                <tbody>
                  {creators.map((c) => (
                    <tr key={c.id}>
                      <td style={{ ...tableStyles.td, fontWeight: 600 }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          {c.avatarUrl && <img src={c.avatarUrl} alt="" style={{ width: 26, height: 26, borderRadius: '50%', objectFit: 'cover' }} />}
                          {c.displayName}
                        </span>
                        {c.subsDisplay && <div style={{ fontSize: 11.5, color: 'var(--fg-4)', fontWeight: 400 }}>{c.subsDisplay}</div>}
                      </td>
                      <td style={tableStyles.td}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <code style={{ fontSize: 12 }}>/takeovers/c/{c.slug}</code>
                          <Button size="sm" variant="ghost" onClick={() => copyCreatorLink(c)}>
                            {copiedSlug === c.slug ? 'Copied ✓' : 'Copy'}
                          </Button>
                        </span>
                      </td>
                      <td style={tableStyles.td}>{c.contacts.filter((x) => x.notify).length} notified</td>
                      <td style={tableStyles.td}>{c.budgetTiers.length}</td>
                      <td style={tableStyles.td}>
                        <StatusPill variant={c.active ? 'ok' : 'paused'}>{c.active ? 'Active' : 'Offline'}</StatusPill>
                      </td>
                      <td style={tableStyles.td}>{fmtDate(c.updatedAt)}</td>
                      <td style={tableStyles.td}>
                        {canManage && (
                          <Button size="sm" variant="subtle" onClick={() => setEditingCreator(c)}>Edit</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        )}

        {tab === 'analytics' && <AnalyticsPanel />}
      </div>

      {openRequestId && (
        <RequestDetail
          requestId={openRequestId}
          canManage={canManage}
          onClose={closeDetail}
          onUpdated={onRequestUpdated}
          notify={notify}
        />
      )}

      {(showCreateCreator || editingCreator) && (
        <CreatorEditor
          creator={editingCreator}
          onClose={() => { setShowCreateCreator(false); setEditingCreator(null); }}
          onSaved={() => refreshCreators()}
          notify={notify}
        />
      )}

      {/* Fixed above the modal layer: create/edit/detail modals raise most of these. */}
      {toastLayer}
    </DashboardFrame>
  );
};

export default TakeoversDashboard;
